import {
  DataTablePagination,
  DataTableSearch,
  Typography,
  DataTable,
  Button,
  Form,
} from '@ui/components';
import { EmployeeTableDataDto } from '@interfaces/models';
import { useCallback, useState, useMemo } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { HasPermission } from '@shared/components';
import { Filters } from '@ui/components/icons';

import { EMPLOYEES_TABLE_FILTERS_DEFAULT_VALUES, useEmployeesTable } from './use-employees-table';
import { EmployeesTableFilters } from '../employees-table-filters/employees-table-filters';
import { EmployeeBulkActions } from '../employees-bulk-actions/employee-bulk-actions';

interface EmployeesTableProps {
  onOpenRemoveEmployeeDialog: (employee: EmployeeTableDataDto) => void;
  onOpenEditEmployeeDrawer: (employee: EmployeeTableDataDto) => void;
}

export const EmployeesTable = (props: EmployeesTableProps) => {
  const { onOpenRemoveEmployeeDialog, onOpenEditEmployeeDrawer } = props;

  const { t } = useTranslation();
  const {
    filtersOptions,
    columnsLength,
    globalFilter,
    filtersFrom,
    totalItems,
    isLoading,
    table,
    state,
  } = useEmployeesTable({
    onOpenRemoveEmployeeDialog,
    onOpenEditEmployeeDrawer,
  });

  const [filtersVisible, setFiltersVisible] = useState(false);

  const selectedRows = useMemo(() => Object.keys(state.rowSelection), [state.rowSelection]);

  const hasSelectedRows = selectedRows.length > 0;

  const handleClearSelection = useCallback(() => {
    table.setRowSelection({});
  }, [table]);

  const toggleFilters = useCallback(() => {
    setFiltersVisible((prev) => !prev);
  }, []);

  const handlePrevPage = useCallback(() => {
    table.previousPage();
  }, [table]);

  const handleNextPage = useCallback(() => {
    table.nextPage();
  }, [table]);

  const handleReset = useCallback(() => {
    filtersFrom.reset(EMPLOYEES_TABLE_FILTERS_DEFAULT_VALUES);
  }, [filtersFrom]);

  return (
    <div className="flex flex-col bg-table-background rounded-lg overflow-hidden">
      <div className="flex items-center px-4 py-3">
        <Typography variant="labelMediumMedium">
          {hasSelectedRows
            ? t('EMPLOYEES.TABLE_SELECTED_EMPLOYEES', {
                postProcess: 'interval',
                count: selectedRows.length,
              })
            : t('EMPLOYEES.TABLE_TOTAL_EMPLOYEES', { count: totalItems })}
        </Typography>

        <div className="ml-auto flex items-center gap-4">
          <DataTableSearch
            placeholder={t('EMPLOYEES.TABLE_SEARCH_PLACEHOLDER')}
            onChange={table.setGlobalFilter}
            value={globalFilter}
          />

          <Button onClick={toggleFilters} variant="ghost">
            <Filters />
            {t('EMPLOYEES.TABLE_FILTERS')}
          </Button>

          <DataTablePagination
            pageIndex={table.getState().pagination.pageIndex}
            pageSize={table.getState().pagination.pageSize}
            prevPageDisabled={!table.getCanPreviousPage()}
            nextPageDisabled={!table.getCanNextPage()}
            prevPage={handlePrevPage}
            nextPage={handleNextPage}
            totalRows={totalItems}
          />
        </div>
      </div>
      {filtersVisible && (
        <Form {...filtersFrom}>
          <EmployeesTableFilters
            controls={filtersFrom.control}
            className="px-4 pb-3 pt-1.5"
            options={filtersOptions}
            reset={handleReset}
          />
        </Form>
      )}

      <HasPermission permissionFeature="EMPLOYEES_EDIT_EMPLOYEE" permissionType="WRITE">
        {hasSelectedRows && (
          <EmployeeBulkActions
            onEmployeeRemove={handleClearSelection}
            selectedRows={selectedRows}
          />
        )}
      </HasPermission>

      <DataTable
        noResultsComponent={
          <Typography variant="labelMediumBook">
            <Trans i18nKey="EMPLOYEES.TABLE_NO_RESULTS" />
          </Typography>
        }
        className="h-full overflow-auto"
        columnsLength={columnsLength}
        isLoading={isLoading}
        table={table}
      />
    </div>
  );
};
